import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Activity,
  Box,
  CheckCircle2,
  Clock3,
  Loader2,
  Play,
  RefreshCw,
  Search,
  Store,
  TimerReset,
  Users,
  XCircle,
} from "lucide-react";
import {
  createStoreRunnerJob,
  getStoreRunnerJobs,
  getStoreRunnerTargets,
  type StoreRunnerJob,
  type StoreRunnerJobStatus,
  type StoreRunnerTarget,
} from "../../services/StoreRunnerService";

const ACTIVE_STATUSES = new Set<StoreRunnerJobStatus>(["queued", "running"]);
const MAX_VISIBLE_STORES = 250;

const STATUS_STYLES: Record<
  StoreRunnerJobStatus,
  { label: string; dot: string; badge: string; icon: typeof Activity }
> = {
  queued: {
    label: "Queued",
    dot: "bg-amber-400",
    badge: "bg-amber-50 text-amber-700 ring-amber-200 dark:bg-amber-900/20 dark:text-amber-300 dark:ring-amber-800",
    icon: Clock3,
  },
  running: {
    label: "Running",
    dot: "bg-blue-500",
    badge: "bg-blue-50 text-blue-700 ring-blue-200 dark:bg-blue-900/20 dark:text-blue-300 dark:ring-blue-800",
    icon: Activity,
  },
  succeeded: {
    label: "Complete",
    dot: "bg-emerald-500",
    badge: "bg-emerald-50 text-emerald-700 ring-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-300 dark:ring-emerald-800",
    icon: CheckCircle2,
  },
  failed: {
    label: "Failed",
    dot: "bg-red-500",
    badge: "bg-red-50 text-red-700 ring-red-200 dark:bg-red-900/20 dark:text-red-300 dark:ring-red-800",
    icon: XCircle,
  },
  timed_out: {
    label: "Timed out",
    dot: "bg-orange-500",
    badge: "bg-orange-50 text-orange-700 ring-orange-200 dark:bg-orange-900/20 dark:text-orange-300 dark:ring-orange-800",
    icon: TimerReset,
  },
  cancelled: {
    label: "Cancelled",
    dot: "bg-gray-400",
    badge: "bg-gray-100 text-gray-600 ring-gray-200 dark:bg-dark-700 dark:text-dark-200 dark:ring-dark-600",
    icon: XCircle,
  },
};

// One row per store. `runner` is the user-store assignment a launched job
// runs under — the one with the most saved categories for that store.
interface StoreRow {
  storeId: string;
  storeName?: string;
  storeAddress?: string;
  postalCode?: string;
  categoryCount: number;
  runner: StoreRunnerTarget;
}

function dedupeStores(targets: StoreRunnerTarget[]): StoreRow[] {
  const byStore = new Map<string, StoreRow>();
  for (const target of targets) {
    const row = byStore.get(target.storeId);
    if (!row) {
      byStore.set(target.storeId, {
        storeId: target.storeId,
        storeName: target.storeName,
        storeAddress: target.storeAddress,
        postalCode: target.postalCode,
        categoryCount: target.categoryCount,
        runner: target,
      });
      continue;
    }
    row.storeName ||= target.storeName;
    row.storeAddress ||= target.storeAddress;
    row.postalCode ||= target.postalCode;
    if (target.categoryCount > row.categoryCount) {
      row.categoryCount = target.categoryCount;
      row.runner = target;
    }
  }
  return [...byStore.values()].sort((a, b) =>
    (a.storeName || a.storeId).localeCompare(b.storeName || b.storeId, "en", { numeric: true })
  );
}

function formatRelativeTime(value?: string) {
  if (!value) return "Not started";
  const timestamp = new Date(value).getTime();
  if (!Number.isFinite(timestamp)) return "Unknown";
  const seconds = Math.max(0, Math.round((Date.now() - timestamp) / 1000));
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

function compactError(error: unknown, fallback: string) {
  return error instanceof Error && error.message ? error.message : fallback;
}

function StatCard({
  label,
  value,
  detail,
  icon: Icon,
  iconClass,
}: {
  label: string;
  value: number | string;
  detail: string;
  icon: typeof Activity;
  iconClass: string;
}) {
  return (
    <div className="rounded-xl bg-white px-3.5 py-3 ring-1 ring-gray-200 shadow-sm dark:bg-dark-800 dark:ring-dark-700">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-gray-400 dark:text-dark-400">{label}</p>
          <p className="mt-1 text-2xl font-bold text-gray-950 dark:text-white">{value}</p>
          <p className="mt-0.5 text-[11px] text-gray-500 dark:text-dark-300">{detail}</p>
        </div>
        <span className={`flex h-8 w-8 items-center justify-center rounded-lg ${iconClass}`}>
          <Icon className="h-4 w-4" />
        </span>
      </div>
    </div>
  );
}

function JobCard({ job }: { job: StoreRunnerJob }) {
  const style = STATUS_STYLES[job.status];
  const StatusIcon = style.icon;
  const progress = job.status === "succeeded" ? 100 : Math.max(0, Math.min(100, job.progress || 0));

  return (
    <article className="rounded-xl bg-white p-3.5 ring-1 ring-gray-200 shadow-sm dark:bg-dark-800 dark:ring-dark-700">
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2">
          <p className="truncate text-[13px] font-semibold text-gray-950 dark:text-white">
            {job.storeName || `Walmart #${job.storeId}`}
          </p>
          <span className={`inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold ring-1 ${style.badge}`}>
            <StatusIcon className={`h-3 w-3 ${job.status === "running" ? "animate-pulse" : ""}`} />
            {style.label}
          </span>
        </div>
        <p className="shrink-0 text-[10px] text-gray-400 dark:text-dark-400">{formatRelativeTime(job.startedAt || job.createdAt)}</p>
      </div>

      <div className="mt-3 flex items-center justify-between gap-2 text-[11px]">
        <span className="font-mono text-[10px] text-gray-400 dark:text-dark-400">#{job.storeId}</span>
        <span className="shrink-0 font-mono font-bold tabular-nums text-gray-700 dark:text-dark-100">{progress}%</span>
      </div>

      <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-gray-100 dark:bg-dark-700">
        <div
          className={`h-full rounded-full transition-[width] duration-500 ${
            job.status === "failed" ? "bg-red-500" : job.status === "timed_out" ? "bg-orange-500" : "bg-blue-600"
          }`}
          style={{ width: `${progress}%` }}
        />
      </div>

      <div className="mt-2.5 flex items-start justify-between gap-3 text-[10px]">
        <p className={`min-w-0 truncate ${job.error ? "text-red-600 dark:text-red-400" : "text-gray-500 dark:text-dark-300"}`}>
          {job.error || job.currentCategory || job.message || "Preparing job"}
        </p>
        {job.categoriesTotal > 0 && (
          <span className="shrink-0 font-mono tabular-nums text-gray-400 dark:text-dark-400">
            {job.categoriesCompleted}/{job.categoriesTotal} categories
          </span>
        )}
      </div>
    </article>
  );
}

export default function StoreRunnerTab() {
  const queryClient = useQueryClient();
  const [retailer] = useState("walmart");
  const [search, setSearch] = useState("");
  const [launchError, setLaunchError] = useState<string | null>(null);

  const targetsQuery = useQuery({
    queryKey: ["store-runner-targets", retailer],
    queryFn: () => getStoreRunnerTargets(retailer),
    staleTime: 60_000,
  });
  const jobsQuery = useQuery({
    queryKey: ["store-runner-jobs", retailer],
    queryFn: () => getStoreRunnerJobs(retailer),
    refetchInterval: 2_500,
  });
  const createJob = useMutation({
    mutationFn: createStoreRunnerJob,
    onMutate: () => setLaunchError(null),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["store-runner-jobs", retailer] }),
    onError: (error) => setLaunchError(compactError(error, "Could not launch the Ultima job")),
  });

  const targets = useMemo(() => targetsQuery.data?.targets ?? [], [targetsQuery.data]);
  const jobs = useMemo(() => jobsQuery.data?.jobs ?? [], [jobsQuery.data]);
  const stores = useMemo(() => dedupeStores(targets), [targets]);
  const activeJobs = useMemo(() => jobs.filter((job) => ACTIVE_STATUSES.has(job.status)), [jobs]);
  const activeStoreIds = useMemo(() => new Set(activeJobs.map((job) => job.storeId)), [activeJobs]);
  const normalizedSearch = search.trim().toLowerCase();
  const filteredStores = useMemo(() => {
    if (!normalizedSearch) return stores;
    return stores.filter((store) =>
      [store.storeId, store.storeName, store.storeAddress, store.postalCode].some((value) =>
        value?.toLowerCase().includes(normalizedSearch)
      )
    );
  }, [normalizedSearch, stores]);
  const visibleStores = filteredStores.slice(0, MAX_VISIBLE_STORES);
  const uniqueUsers = useMemo(() => new Set(targets.map((target) => target.userId.toLowerCase())).size, [targets]);
  const queued = jobs.filter((job) => job.status === "queued").length;
  const running = jobs.filter((job) => job.status === "running").length;

  const launch = (store: StoreRow) => {
    createJob.mutate({
      retailer: store.runner.retailer,
      userId: store.runner.userId,
      storeId: store.storeId,
    });
  };

  const refreshing = targetsQuery.isFetching || jobsQuery.isFetching;
  const pageError =
    (targetsQuery.error && compactError(targetsQuery.error, "Could not load store targets")) ||
    (jobsQuery.error && compactError(jobsQuery.error, "Could not load jobs"));

  return (
    <div className="space-y-4">
      <header className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-blue-600 ring-1 ring-blue-100 dark:bg-blue-900/20 dark:text-blue-300 dark:ring-blue-900/40">
            <Box className="h-4.5 w-4.5" />
          </span>
          <div>
            <h2 className="text-lg font-bold tracking-tight text-gray-950 dark:text-white">Store runner</h2>
            <p className="text-xs text-gray-500 dark:text-dark-300">Launch an ad-hoc Ultima container per store, with a one-hour hard stop</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <label className="sr-only" htmlFor="store-runner-retailer">Retailer</label>
          <select
            id="store-runner-retailer"
            value={retailer}
            disabled
            className="h-8 rounded-lg bg-white px-2.5 text-xs font-semibold text-gray-700 ring-1 ring-gray-200 outline-none disabled:cursor-default dark:bg-dark-800 dark:text-dark-100 dark:ring-dark-700"
          >
            <option value="walmart">Walmart</option>
          </select>
          <button
            type="button"
            onClick={() => {
              targetsQuery.refetch();
              jobsQuery.refetch();
            }}
            className="flex h-8 items-center gap-1.5 rounded-lg bg-white px-3 text-xs font-semibold text-gray-700 ring-1 ring-gray-200 transition-colors hover:bg-gray-50 dark:bg-dark-800 dark:text-dark-100 dark:ring-dark-700 dark:hover:bg-dark-700"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>
      </header>

      <div className="grid grid-cols-2 gap-3 2xl:grid-cols-4">
        <StatCard label="Stores" value={stores.length} detail="Unique saved stores" icon={Store} iconClass="bg-blue-50 text-blue-600 dark:bg-blue-900/20 dark:text-blue-300" />
        <StatCard label="Users" value={uniqueUsers} detail="Active Walmart plans" icon={Users} iconClass="bg-violet-50 text-violet-600 dark:bg-violet-900/20 dark:text-violet-300" />
        <StatCard label="Running" value={running} detail="Live containers" icon={Activity} iconClass="bg-emerald-50 text-emerald-600 dark:bg-emerald-900/20 dark:text-emerald-300" />
        <StatCard label="Queued" value={queued} detail="Waiting for capacity" icon={Clock3} iconClass="bg-amber-50 text-amber-600 dark:bg-amber-900/20 dark:text-amber-300" />
      </div>

      {(pageError || launchError) && (
        <div className="rounded-xl bg-red-50 px-4 py-3 text-xs font-medium text-red-700 ring-1 ring-red-200 dark:bg-red-900/20 dark:text-red-300 dark:ring-red-800">
          {launchError || pageError}
        </div>
      )}

      <div className="grid min-w-0 gap-4 xl:grid-cols-[minmax(0,1fr)_360px]">
        <section className="min-w-0 overflow-hidden rounded-2xl bg-white ring-1 ring-gray-200 shadow-sm dark:bg-dark-800 dark:ring-dark-700">
          <div className="flex flex-col gap-3 border-b border-gray-100 px-4 py-3.5 dark:border-dark-700 2xl:flex-row 2xl:items-center 2xl:justify-between">
            <div>
              <h3 className="text-sm font-bold text-gray-950 dark:text-white">Eligible Walmart stores</h3>
              <p className="mt-0.5 text-[11px] text-gray-500 dark:text-dark-300">One row per unique saved store</p>
            </div>
            <div className="relative w-full sm:w-72">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
              <input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search store, address, ZIP…"
                className="h-9 w-full rounded-lg bg-gray-50 pl-9 pr-3 text-xs text-gray-900 outline-none ring-1 ring-gray-200 placeholder:text-gray-400 focus:ring-2 focus:ring-blue-500 dark:bg-dark-900 dark:text-white dark:ring-dark-600"
              />
            </div>
          </div>

          {targetsQuery.isLoading ? (
            <div className="flex h-64 items-center justify-center gap-2 text-xs text-gray-500 dark:text-dark-300">
              <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
              Loading active stores
            </div>
          ) : visibleStores.length === 0 ? (
            <div className="flex h-64 flex-col items-center justify-center px-5 text-center">
              <Store className="h-8 w-8 text-gray-300 dark:text-dark-500" />
              <p className="mt-3 text-sm font-semibold text-gray-700 dark:text-dark-100">No matching stores</p>
              <p className="mt-1 text-xs text-gray-500 dark:text-dark-300">Try a different store name, number, or ZIP.</p>
            </div>
          ) : (
            <>
              <div className="max-h-[610px] overflow-auto">
                <table className="w-full table-fixed text-left">
                  <colgroup>
                    <col className="w-[67%]" />
                    <col className="w-[13%]" />
                    <col className="w-[20%]" />
                  </colgroup>
                  <thead className="sticky top-0 z-10 bg-gray-50/95 text-[10px] font-bold uppercase tracking-[0.12em] text-gray-400 backdrop-blur dark:bg-dark-900/95 dark:text-dark-400">
                    <tr>
                      <th className="px-4 py-2.5">Store</th>
                      <th className="px-2 py-2.5">Cats</th>
                      <th className="px-4 py-2.5 text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-dark-700">
                    {visibleStores.map((store) => {
                      const active = activeStoreIds.has(store.storeId);
                      const launching = createJob.isPending && createJob.variables?.storeId === store.storeId;
                      return (
                        <tr key={store.storeId} className="group hover:bg-gray-50/70 dark:hover:bg-dark-700/40">
                          <td className="px-4 py-3">
                            <div className="flex items-start gap-2.5">
                              <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-blue-50 text-blue-600 dark:bg-blue-900/20 dark:text-blue-300">
                                <Store className="h-3.5 w-3.5" />
                              </span>
                              <div className="min-w-0">
                                <p className="truncate text-xs font-semibold text-gray-800 dark:text-dark-100">
                                  {store.storeName || `Walmart #${store.storeId}`}
                                  {store.storeName && !store.storeName.includes(`#${store.storeId}`) && (
                                    <span className="ml-1.5 font-mono text-[10px] font-normal text-gray-400">#{store.storeId}</span>
                                  )}
                                </p>
                                <p className="mt-0.5 truncate text-[10px] text-gray-500 dark:text-dark-300">{store.storeAddress || store.postalCode || "Address unavailable"}</p>
                              </div>
                            </div>
                          </td>
                          <td className="px-2 py-3">
                            <span className="inline-flex rounded-full bg-gray-100 px-2 py-0.5 font-mono text-[10px] font-bold tabular-nums text-gray-600 dark:bg-dark-700 dark:text-dark-200">
                              {store.categoryCount}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <button
                              type="button"
                              onClick={() => launch(store)}
                              disabled={active || Boolean(launching)}
                              aria-label={active ? "Job active" : launching ? "Starting job" : `Run Walmart #${store.storeId}`}
                              title={active ? "A job is already active for this store" : "Run Ultima"}
                              className={`inline-flex h-8 min-w-16 items-center justify-center gap-1.5 rounded-lg px-2.5 text-[11px] font-bold transition-colors ${
                                active
                                  ? "cursor-default bg-blue-50 text-blue-600 ring-1 ring-blue-200 dark:bg-blue-900/20 dark:text-blue-300 dark:ring-blue-800"
                                  : "bg-gray-950 text-white hover:bg-blue-600 disabled:opacity-60 dark:bg-white dark:text-gray-950 dark:hover:bg-blue-400"
                              }`}
                            >
                              {launching ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : active ? <Activity className="h-3.5 w-3.5 animate-pulse" /> : <Play className="h-3.5 w-3.5 fill-current" />}
                              {active ? "Active" : launching ? "Starting" : "Run"}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {filteredStores.length > MAX_VISIBLE_STORES && (
                <p className="border-t border-gray-100 px-4 py-2.5 text-center text-[10px] text-gray-400 dark:border-dark-700 dark:text-dark-400">
                  Showing the first {MAX_VISIBLE_STORES} matches. Refine your search to narrow the list.
                </p>
              )}
            </>
          )}
        </section>

        <aside className="min-w-0">
          <div className="rounded-2xl bg-gray-50 p-3 ring-1 ring-gray-200 dark:bg-dark-900 dark:ring-dark-700">
            <div className="flex items-center justify-between px-1 pb-3">
              <div>
                <h3 className="text-sm font-bold text-gray-950 dark:text-white">Recent jobs</h3>
                <p className="mt-0.5 text-[10px] text-gray-500 dark:text-dark-300">Live updates every 2.5 seconds</p>
              </div>
              {activeJobs.length > 0 && (
                <span className="inline-flex items-center gap-1.5 rounded-full bg-blue-50 px-2 py-1 text-[10px] font-bold text-blue-700 ring-1 ring-blue-200 dark:bg-blue-900/20 dark:text-blue-300 dark:ring-blue-800">
                  <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-blue-500" />
                  {activeJobs.length} active
                </span>
              )}
            </div>
            {jobsQuery.isLoading ? (
              <div className="flex h-44 items-center justify-center">
                <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
              </div>
            ) : jobs.length === 0 ? (
              <div className="flex h-44 flex-col items-center justify-center rounded-xl border border-dashed border-gray-300 bg-white px-5 text-center dark:border-dark-600 dark:bg-dark-800">
                <Box className="h-7 w-7 text-gray-300 dark:text-dark-500" />
                <p className="mt-2.5 text-xs font-semibold text-gray-700 dark:text-dark-100">No jobs yet</p>
                <p className="mt-1 text-[10px] leading-relaxed text-gray-500 dark:text-dark-300">Choose a store and press Run to launch Ultima.</p>
              </div>
            ) : (
              <div className="max-h-[690px] space-y-2.5 overflow-auto p-0.5">
                {jobs.map((job) => <JobCard key={job.id} job={job} />)}
              </div>
            )}
            <div className="mt-3 flex items-center gap-2 rounded-lg bg-white px-3 py-2 text-[10px] text-gray-500 ring-1 ring-gray-200 dark:bg-dark-800 dark:text-dark-300 dark:ring-dark-700">
              <TimerReset className="h-3.5 w-3.5 shrink-0 text-orange-500" />
              Every container has an independent one-hour hard stop.
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
